

// import { InputNumber } from 'antd';
import { useMemo, useState } from 'react';
import { Button, Space, Tooltip } from 'antd';
import { InputBoolean, InputCardLookup, InputChoiceMulti, InputColor, InputCurrency, InputDate, InputEmail, InputFile, InputLocation, InputMeasurement, InputNum, InputPhone, InputRating, InputRichText, InputSegmented, InputSelect, InputText, InputTextArea, InputTime, InputUser } from '../Input';
import { CardIconSwatch } from '../cards/CardVisuals';
import DrivenRuleModal from '../items/DrivenRuleModal';
import DrivenSourceModal from '../items/DrivenSourceModal';
import CalculatedValueModal from '../items/CalculatedValueModal';
import { entryScopeOf, formulaElementResolver, formulaSummary, isFormulaDriven, previewFormula, previewValuesOf, sameStoredValue } from '../../utils/formulaTokens';
import MultiDateField from '../items/MultiDateField';
import { isDateType, minuteStepOf, resolveBounds } from '../../utils/dateBounds';
import { allowsMultipleDates, formatDateValue, precisionOf, weekdaysOf } from '../../utils/dateOptions';
import { formatMeasurement } from '../../utils/units';
import { formatCurrency } from '../../utils/currency';
import { formatBooleanValue } from '../../utils/elements';
import { hasValueValidator, validateElementValue } from '../../utils/elementValidation';
import { normalizeUrl } from '../../utils/elementFormat/url';
import { countChars, maxLengthOf } from '../../utils/elementFormat/text';
import { storedTextValue } from '../../utils/textRules';
import { MeasurementConversionsButton } from '../common/MeasurementConversions';
import { ValueActionButton } from '../common/ValueAction';
import ColorSwatch, { COLOR_PRESETS } from '../common/ColorSwatch';
import UserValue from '../users/UserValue';
import { formatValue } from '../items/itemValueDisplay';
import { canManage } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';
import { scanConfig } from '../../services/scanLookup';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { EYE, GEAR, LOCK } from '../icons';
import FieldHelp, { elementHelpText } from '../common/FieldHelp';
import { countedFromSentence, isConfirmationsElement, peopleCount } from '../../utils/confirmationValues';
import { isRepeatingGroup } from '../../config/repeatingGroups';
import RepeatingGroupField from '../items/RepeatingGroupField';
import { choiceListOf } from '../../utils/choiceValues';
import { drivenSourceLabel } from '../../utils/drivenSummary';
import MaskedValue from '../common/MaskedValue';
import { isMaskedElement } from '../../utils/maskedElements';
// import { CHEVRON_DOWN, CHEVRON_UP } from '../icons';

// Data types this form knows how to render. Anything else (including a
// not-yet-chosen dataType) falls back to a plain text input so a required
// element can never become unfillable.
// A "Me too" count (KPR-390) is listed though it has no input: it renders as a
// value (ConfirmationsElementValue), and must never fall through to a text box.
const RENDERED_TYPES = [ 'text-small', 'choice', 'text-large', 'rich-text', 'date', 'date-time', 'time', 'number', 'decimal', 'integer', 'measurement', 'currency', 'boolean', 'phone', 'email', 'location', 'url', 'rating', 'card-lookup', 'user', 'color', 'file', 'confirmations' ];

const SYSTEM_SOURCE_LABELS = { $createdAt: 'Created', $updatedAt: 'Updated' };

/**
 * A SERVER-NUMBERED element (`options.sequence`): the work order number, the
 * invoice number, the ticket number. The server allocates it once, when the
 * record is created, and nothing afterwards can move it.
 *
 * It renders as a VALUE, never as an input, in the same visual vocabulary as
 * a driven value — the lock beside the label, the value in a read row —
 * because it is the same promise to the reader: what is in this field is not
 * yours to type, and an editable-looking box would be a lie that swallows
 * the typing.
 *
 * On a NEW item there is nothing to show yet, so it says what will happen
 * instead of showing a blank: the number does not exist until the record
 * does, and showing a provisional one would be worse than showing none — two
 * people with the form open would each be shown a number only one of them
 * will get.
 *
 * On a SAVED item it shows the number the way every other reader shows it:
 * through the vendored formatter, with the element's own prefix and padding.
 * The stored value is the bare number — that is what keeps the list in
 * numeric order — and this is where the two facts are reconciled for the
 * person looking at the form.
 */
function SequenceElementValue ( { element, value, labelBadge } ) {
	const hasValue = value !== undefined && value !== null && value !== '';
	const formatted = hasValue
		? ( formatValue( element, value ) || String( value ) )
		: null;
	const tooltip = hasValue
		? 'Assigned by keepr when this record was created. It never changes.'
		: 'Assigned by keepr when you save. Nobody types this one.';

	return (
		<div className='mb-2 element-field__driven'>
			<div className='field-label-row'>
				<FieldHelp text={elementHelpText( element )} label={element?.label?.singular}>
					<label className='d-block' htmlFor={element?.name} style={{ minWidth: 'fit-content', height: 'fit-content' }}>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							{labelBadge ? (
								<span style={{ display: 'inline-flex', alignItems: 'center' }}>{labelBadge}</span>
							) : null}
							<span>{element?.label?.singular}</span>
						</span>
					</label>
				</FieldHelp>
				<Tooltip title={tooltip}>
					<span
						className='element-field__driven-icon'
						role='img'
						aria-label={tooltip}
						style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--ink-muted)' }}
					>
						<LOCK />
					</span>
				</Tooltip>
			</div>
			<div id={element?.name} className='element-field__driven-value' data-testid='sequence-value'>
				{ hasValue ? formatted : <span style={{ color: 'var(--ink-muted)' }}>Assigned when you save</span> }
			</div>
		</div>
	);
}

/**
 * A MANUAL ORDER (KPR-1396, `options.rank`): the order people put items in.
 * keepr writes it when the item is made and when it is moved in a list sorted
 * by it; the number itself means nothing to read, so the form says where the
 * order is changed instead of showing it, in a driven value's vocabulary.
 */
function RankElementValue ( { element, labelBadge } ) {
	const label = element?.label?.singular || element?.name;
	const text = `Set by moving the item in a list sorted by ${label}.`;
	return (
		<div className='mb-2 element-field__driven'>
			<div className='field-label-row'>
				<FieldHelp text={elementHelpText( element )} label={element?.label?.singular}>
					<label className='d-block' htmlFor={element?.name} style={{ minWidth: 'fit-content', height: 'fit-content' }}>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							{labelBadge ? (
								<span style={{ display: 'inline-flex', alignItems: 'center' }}>{labelBadge}</span>
							) : null}
							<span>{label}</span>
						</span>
					</label>
				</FieldHelp>
				<Tooltip title={text}>
					<span
						className='element-field__driven-icon'
						role='img'
						aria-label={text}
						style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--ink-muted)' }}
					>
						<LOCK />
					</span>
				</Tooltip>
			</div>
			<div id={element?.name} className='element-field__driven-value' data-testid='rank-value'>
				<span style={{ color: 'var(--ink-muted)' }}>{text}</span>
			</div>
		</div>
	);
}

/**
 * A "ME TOO" element (KPR-390): how many people said so. Nobody types it —
 * keepr counts it from each person's own confirm and take-back, which live in
 * the read view (components/items/ConfirmationsValue.js), not here — so the
 * form shows the count the way a numbered element shows its number: a value
 * with the lock beside the label, never an input. The form never sends it
 * (utils/confirmationValues.js withoutConfirmations); the API keeps the count
 * whatever a save carries.
 */
function ConfirmationsElementValue ( { element, value, labelBadge } ) {
	const counted = peopleCount( value );
	const tooltip = countedFromSentence( element );
	return (
		<div className='mb-2 element-field__driven'>
			<div className='field-label-row'>
				<FieldHelp text={elementHelpText( element )} label={element?.label?.singular}>
					<label className='d-block' htmlFor={element?.name} style={{ minWidth: 'fit-content', height: 'fit-content' }}>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							{labelBadge ? (
								<span style={{ display: 'inline-flex', alignItems: 'center' }}>{labelBadge}</span>
							) : null}
							<span>{element?.label?.singular || element?.name}</span>
						</span>
					</label>
				</FieldHelp>
				<Tooltip title={tooltip}>
					<span
						className='element-field__driven-icon'
						role='img'
						aria-label={tooltip}
						style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--ink-muted)' }}
					>
						<LOCK />
					</span>
				</Tooltip>
			</div>
			<div id={element?.name} className='element-field__driven-value' data-testid='confirmations-value'>
				{ counted || <span style={{ color: 'var(--ink-muted)' }}>None yet</span> }
			</div>
		</div>
	);
}

/**
 * A DRIVEN element: its value is maintained by one of the collection's
 * automations, recomputed whenever a source item changes. It renders as a
 * value, never as an input — the server overwrites whatever a user submitted
 * for it, so an editable-looking field would be a lie that silently discards
 * typing.
 *
 * The tooltip names the source because the value is denormalized: a reader of
 * this item sees data derived from items they may have no access to, and an
 * editor of those source items changes this field without ever opening this
 * item. That trade is sanctioned at authoring time (the driven-from modal
 * spells it out); this is where it is spelled out at the point of effect.
 *
 * Two affix actions sit at the right of the value, following the app's
 * Space.Compact convention (actions rightmost):
 *  - View source record — always offered, because "where did this come from?"
 *    is the question a driven value provokes for every reader.
 *  - Manage this rule — manage-role only, mirroring the API's manage-gated
 *    automation routes. HIDDEN (not disabled) for everyone else: a read/write
 *    sharee has nothing to do there, and a greyed button would only invite a
 *    click into a 403. A calculated COLUMN of a repeating group has no rule
 *    of its own to open: its group has one managed rule, so a column is
 *    offered the eye and not this.
 */
function DrivenElementValue ( { element, value, labelBadge, event, cardMeta, groupColumn = null } ) {

	const drivenFrom = element?.options?.drivenFrom || {};
	const isFormula = isFormulaDriven( element );

	// Optional so this component (and the form around it) still renders with no
	// card cache — outside the provider the tooltip simply drops the names.
	const cardDefinitions = useOptionalCardDefinitions();

	// Null outside CollectionsLayout (quick-create dialogs, tests). No
	// collection means no way to know the caller manages it, so the rule action
	// is simply not offered.
	const collection = useCollectionContext();
	const canManageCollection = !! collection && canManage( collection );

	const [ sourceOpen, setSourceOpen ] = useState( false );
	const [ ruleOpen, setRuleOpen ] = useState( false );

	// Nothing can link to an id that does not exist yet, so an unsaved item has
	// no source records to show — the action stays visible but disabled, and
	// says why.
	const isSaved = !! normalizeId( event?._id );
	// A calculated value has no source RECORD to show — its inputs are right
	// here on the form — so the eye explains the calculation instead, saved
	// or not.
	const sourceTooltip = isFormula ? 'See how this is calculated' : ( isSaved ? 'View the source record' : 'Save the item first' );
	const sourceCardId = normalizeId( drivenFrom.sourceCardId );
	const sourceCard = sourceCardId ? cardDefinitions?.cardsById?.[ sourceCardId ] : null;
	const sourceElements = ( sourceCardId && cardDefinitions?.getElementsForCard )
		? ( cardDefinitions.getElementsForCard( sourceCardId ) || [] )
		: [];
	const sourceElementLabel = SYSTEM_SOURCE_LABELS[ drivenFrom.sourceElementName ]
		|| drivenSourceLabel( drivenFrom.sourceElementName, sourceElements );

	const cardId = normalizeId( cardMeta?._id || cardMeta?.id );
	const cardsById = cardDefinitions?.cardsById;
	const cardMetaElements = cardMeta?.elements;
	// Last-definition-wins, as the engine reads the card (the app cache's own
	// resolver keeps the ancestor's definition of a re-declared name).
	const getElementsForCard = useMemo( () => formulaElementResolver( { cardsById }, cardMeta ? [ cardMeta ] : [] ), [ cardsById, cardMeta ] );
	const mergedElements = useMemo( () => {
		const resolved = cardId ? getElementsForCard( cardId ) : [];
		return resolved.length ? resolved : ( cardMetaElements || [] );
	}, [ cardId, getElementsForCard, cardMetaElements ] );

	// A calculated COLUMN of a repeating group (KPR-828) is read in one entry's
	// scope: its entry's other columns first, then the item's elements, no
	// lookup followed. `groupColumn` is { group, columns, parentValues }: the
	// group, the columns of its entries, and the item's values the group's
	// formulas read. The entry itself is `event`.
	const entryScope = useMemo(
		() => ( groupColumn && isFormula ? entryScopeOf( groupColumn.group, groupColumn.columns, { excludeName: element?.name } ) : null ),
		[ groupColumn, isFormula, element?.name ]
	);

	const tooltip = isFormula
		? `Calculated: ${formulaSummary( drivenFrom.formula || '', mergedElements, getElementsForCard, entryScope ? { columns: groupColumn.columns } : undefined )} — worked out automatically from ${entryScope ? 'this entry and its item' : 'this record\'s fields'}.`
		: ( sourceCard?.name && sourceElementLabel )
			? `Driven from ${sourceCard.name} · ${sourceElementLabel} — updated automatically by the collection's automation.`
			: 'Updated automatically by the collection\'s automation.';

	// The "after save" hint: the same shared module the API runs, over the
	// form's CURRENT values. Only for formulas that read this record alone —
	// a dot-walk needs the linked record, which the form does not hold.
	const preview = useMemo( () => {
		if ( ! isFormula ) return null;
		const out = previewFormula( drivenFrom.formula, {
			mergedElements,
			getElementsForCard,
			targetElement: element,
			values: entryScope ? ( groupColumn.parentValues || {} ) : previewValuesOf( event, mergedElements ),
			blanks: drivenFrom.blanks,
			now: new Date(),
			...( entryScope ? { entry: entryScope, entryValues: event?.elements || {} } : {} ),
		} );
		if ( ! out.ok || out.analysis.walks.length ) return null;
		return out;
	}, [ isFormula, drivenFrom.formula, drivenFrom.blanks, mergedElements, getElementsForCard, element, event, entryScope, groupColumn ] );
	// A masked calculated value (KPR-1348) never previews its next value in the clear.
	const showPreview = !! preview && ! sameStoredValue( preview.value, value ) && ! isMaskedElement( element );

	const formatted = ( () => {
		if ( value === undefined || value === null || value === '' ) return '—';
		// A masked element (KPR-1348): its mask, with Show and Copy.
		if ( isMaskedElement( element ) ) return <MaskedValue element={element} value={value} resetKey={event?._id} />;
		if ( typeof value === 'boolean' ) return formatBooleanValue( element, value );
		// A date reads through its own formatter BEFORE the array case, so a
		// driven list of dates prints as dates and not as raw stored strings.
		if ( element?.dataType === 'date' || element?.dataType === 'date-time' ) {
			return formatDateValue( element, value ) || '—';
		}
		// A driven user value reads as the people, not their ids.
		if ( element?.dataType === 'user' ) {
			return <UserValue element={element} value={value} collectionId={event?.collection_id || event?.collectionId} />;
		}
		if ( Array.isArray( value ) ) return value.length ? value.map( entry => String( entry ) ).join( ', ' ) : '—';
		if ( element?.dataType === 'measurement' ) return formatMeasurement( value, element?.options || {} ) || '—';
		if ( element?.dataType === 'currency' ) return formatCurrency( value, element?.options || {} ) || '—';
		// A location is an OBJECT and a phone is stored in a spelling nobody
		// dials; both read through the shared formatters, as everywhere else.
		// So does a number (decimals, padding, a prefix, and PR 7's percent
		// and grouping): a calculated margin reads "12.5%" here as in the list.
		if ( [ 'location', 'phone', 'number', 'decimal', 'integer' ].includes( element?.dataType ) ) {
			return formatValue( element, value ) || '—';
		}
		// A driven color reads as its swatch and its hex, as on every other
		// read surface.
		if ( element?.dataType === 'color' ) return <><ColorSwatch value={value} />{String( value )}</>;
		return String( value );
	} )();

	return (
		<div className='mb-2 element-field__driven'>
			{/* The lock sits BESIDE the label, not inside it, so hovering it shows
			    only its own tooltip and never the help text's as well; the help "?"
			    lives in the same row for the same reason. */}
			<div className='field-label-row'>
				<FieldHelp text={elementHelpText( element )} label={element?.label?.singular}>
					<label className='d-block' htmlFor={element?.name} style={{ minWidth: 'fit-content', height: 'fit-content' }}>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							{labelBadge ? (
								<span style={{ display: 'inline-flex', alignItems: 'center' }}>{labelBadge}</span>
							) : null}
							<span>{element?.label?.singular}</span>
						</span>
					</label>
				</FieldHelp>
				<Tooltip title={tooltip}>
					<span
						className='element-field__driven-icon'
						role='img'
						aria-label={tooltip}
						style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--ink-muted)' }}
					>
						<LOCK />
					</span>
				</Tooltip>
			</div>
			{/* Affix order matches InputCardLookup's row exactly — preview eye
			    LEFTMOST, value in the middle, management action rightmost — so
			    every record-preview eye in the app sits in the same place. */}
			<Space.Compact className='input-affix-group' block>
				<Tooltip title={sourceTooltip}>
					<Button
						type='default'
						size='large'
						icon={<EYE size={16} />}
						disabled={! isFormula && ! isSaved}
						onClick={() => setSourceOpen( true )}
						aria-label={isFormula ? 'See how this is calculated' : 'View the source record'}
					/>
				</Tooltip>
				<div id={element?.name} className='element-field__driven-value input-affix-group__input'>
					{formatted}
					{ showPreview ? (
						<span className='element-field__driven-preview' data-testid='driven-preview' title='What the next save will store'>
							→ {preview.text}
						</span>
					) : null }
					{ element?.dataType === 'measurement' ? (
						<MeasurementConversionsButton element={element} value={value} />
					) : null }
					{/* A driven phone / email / location / URL carries its
					    action here too: the row is a read surface, and the
					    reader cannot type into it. */}
					<ValueActionButton element={element} value={value} />
				</div>
				{ canManageCollection && ! groupColumn ? (
					<Tooltip title='Manage this rule'>
						<Button
							type='default'
							size='large'
							icon={<GEAR size={16} />}
							onClick={() => setRuleOpen( true )}
							aria-label='Manage this rule'
						/>
					</Tooltip>
				) : null }
			</Space.Compact>

			{ sourceOpen && isFormula ? (
				<CalculatedValueModal
					groupColumn={groupColumn}
					open={sourceOpen}
					onClose={() => setSourceOpen( false )}
					element={element}
					item={event}
					cardMeta={cardMeta}
				/>
			) : null }
			{ sourceOpen && ! isFormula ? (
				<DrivenSourceModal
					open={sourceOpen}
					onClose={() => setSourceOpen( false )}
					element={element}
					item={event}
					cardMeta={cardMeta}
				/>
			) : null }
			{ ruleOpen ? (
				<DrivenRuleModal
					open={ruleOpen}
					onClose={() => setRuleOpen( false )}
					element={element}
					item={event}
					cardMeta={cardMeta}
				/>
			) : null }
		</div>
	);

}

export default function EventFormElement( {
	event,
	element,
	value,
	onChange,
	cardMeta,
	showIconBadge = true,
	errorMessage = null,
	// Every element this form can see (the card chain's, merged). A date
	// bound may name ANOTHER element — "no earlier than Start" — so the field
	// has to be able to look one up and read its current value. Absent, the
	// card that declared this element stands in, which is the same list for
	// every card without a parent.
	siblingDefs = null,
	// The item's element map as SAVED (null on a new item): the echo rule's
	// input, so a link or a paragraph the item already holds from before a
	// rule is not flagged on blur — the API saves it unchanged.
	storedElements = null,
	// Shown, not editable: a field a group built from records reads, which
	// this person cannot change (keepr-api owner ruling b79).
	disabled = false,
	// A file element (PR 10b): the collection's attachments switch, as the
	// form knows it — false says "Files are off in this collection." in the
	// field's place; null (not known yet) shows the input.
	attachmentsOn = null,
	// A COLUMN of a repeating group (KPR-680) is this same field, once per
	// entry: `idPrefix` keeps its DOM ids unique across the entries, and its
	// change is still reported under the column's own name.
	idPrefix = '',
	// A FILE column of a repeating group (KPR-934): { item, group, entryKey } -
	// the item whose `files` facts describe the entry's files (`event` here is
	// the entry), the group the upload names beside the column, and the entry it
	// belongs to, so the form's held uploads are kept per entry.
	entryFile = null,
	// A repeating group's: changes when the form starts over for another item ("Save & add another").
	resetKey = 0,
	// A CALCULATED column of a repeating group (KPR-828): { group, columns,
	// parentValues } — the scope its formula is read, previewed and explained
	// in: this entry (`event`), the group's other columns, and the item.
	groupColumn = null,
	// A repeating group's own needs (RepeatingGroupField): the error map the
	// form keeps (`<group>.<entry>.<column>` beside `<group>`), the way to
	// clear some of it, the collection's time zone (a new entry's defaults),
	// and the reader of a lookup column's title (an entry's summary line).
	entryErrors = null,
	onClearErrors = null,
	zone = undefined,
	resolveLookupValue = null,
} ) {

	//

	const options = element.options || { choices: [] };

	// A Choice's list, and nothing else's: a short text that carries a list
	// (kept from an earlier life as a Choice, KPE-033) is a text box.
	const choices = choiceListOf( element );

	const hasChoices = choices.length > 0;

	// 'selectIcons' is the legacy flag written by the collection form;
	// 'preferChoiceIcons' is written by the card definition form.
	const hasSelectIcons = !! ( options?.selectIcons || options?.preferChoiceIcons );

	// "Allow multiple" on a Choice (docs/plans/data-types-second-pass.md §3.5):
	// the multiple Select over the choice chips, whatever the list's length —
	// a segmented control holds one value.
	const isMultiChoice = element.dataType === 'choice' && options?.allowMultiple === true;

	// 2–5 options render as a segmented control (platform form spec) —
	// unless the legacy icon-tile treatment was explicitly chosen.
	const isSegmented = choices.length >= 2 && choices.length <= 5 && ! hasSelectIcons;

	const isRequired = !! options?.required;

	const isDriven = !! options?.drivenFrom;

	// Server-numbered: read-only for the same reason a driven value is, and
	// rendered with the same vocabulary. Checked BEFORE `isDriven` below
	// only because the two can never both be true — the API refuses the
	// pairing (400 invalid_options) precisely so this form never has to pick.
	const isSequenced = !! options?.sequence;
	// A manual order (KPR-1396): keepr's, like a number it issues.
	const isRanked = options?.rank === true;

	// A "Me too" count: read-only, as a numbered element is (KPR-390).
	const isCounted = isConfirmationsElement( element );

	// Off unless the card author turned it on for this element — see
	// getAutofillProps in Input.js for why that is the default.
	const allowAutocomplete = !! options?.enableAutocomplete;

	// Inline feedback, one field at a time: leaving a field checks the value
	// against the same rules the API enforces on write
	// (utils/elementValidation), so a refusal is seen where it can be fixed
	// instead of as a toast after Save. Only the types that HAVE rules
	// subscribe; everything else keeps a plain onBlur-free input.
	const [ blurError, setBlurError ] = useState( null );

	// What a date bound is judged against: every element of the row, and every
	// value the row currently holds.
	const validationContext = useMemo( () => ( {
		elements: siblingDefs || cardMeta?.elements || [],
		values: event?.elements || {},
		stored: storedElements,
	} ), [ siblingDefs, cardMeta?.elements, event?.elements, storedElements ] );

	// A link is shown the way it will be STORED the moment the field is left:
	// a bare host gets its "https://" (utils/elementFormat/url.js, the rule
	// the API writes with), so what the person sees is what is saved.
	// A short text is shown the way it will be stored too: trimmed, in
	// capitals or small letters, as its element says (PR 2's `trim` and
	// `case`, utils/elementFormat/text.js normalizeText) — except the value
	// the item already stores, which the API saves unchanged (the echo).
	const isStoredValue = !! storedElements
		&& Object.prototype.hasOwnProperty.call( storedElements, element.name )
		&& storedElements[ element.name ] === value;
	const handleBlur = hasValueValidator( element.dataType )
		? () => {
			let checked = value;
			if ( element.dataType === 'url' && typeof value === 'string' && value.trim() ) {
				const normalized = normalizeUrl( value );
				if ( normalized.valid && normalized.value !== value ) {
					checked = normalized.value;
					onChange( element.name, checked );
				}
			}
			if ( element.dataType === 'text-small' && typeof value === 'string' && ! isStoredValue ) {
				const normalized = storedTextValue( value, element );
				if ( normalized !== value ) {
					checked = normalized;
					onChange( element.name, checked );
				}
			}
			setBlurError( validateElementValue( element, checked, validationContext ) );
		}
		: undefined;

	// The author's own maximum length, counted as the API counts it (code
	// points: an emoji is one): antd's count under the field. A value the
	// item already stores past it is shown without the limit — the echo
	// saves it.
	const ownLimit = maxLengthOf( element.dataType, options || {} );
	const textCount = ownLimit !== null
		? { show: true, max: isStoredValue ? undefined : ownLimit, strategy: countChars }
		: undefined;

	const handleChange = ( name, value ) => {
		// Typing fixes things: a message this field raised on blur clears the
		// moment the value changes, rather than sitting under a field the
		// reader has already corrected. A message the SUBMIT raised
		// (errorMessage) is the form's, and only the next submit clears it.
		if ( blurError ) setBlurError( null );
		// A column reports under its own name, whatever its DOM id carried.
		onChange( idPrefix ? element.name : name, value );
	};

	// ---- dates: bounds, and a message that tracks them ----

	const isDate = isDateType( element.dataType );
	const bounds = useMemo(
		() => ( isDate ? resolveBounds( element, validationContext ) : { min: null, max: null } ),
		[ isDate, element, validationContext ]
	);
	// A date field commits WHOLE values — the calendar, or text the grammar
	// understood — never a half-typed one, so its message is computed live
	// rather than latched on blur. That is what makes a bound that names
	// another element re-check BOTH fields the moment either changes: the
	// other field's value is in the context above, so this one re-renders
	// with it.
	//
	// A MULTI date is the exception: it has as many messages as it has
	// entries, and `MultiDateField` prints each under its own row. One
	// sentence for six values could not say which value it was about.
	const isMultiDate = allowsMultipleDates( element );
	const boundError = ( isDate && ! isMultiDate ) ? validateElementValue( element, value, validationContext ) : null;

	// The form's own message wins: it is the one the reader just pressed Save
	// against, and it names the same field.
	const message = errorMessage || blurError || boundError || null;
	const dateProps = isDate ? {
		min: bounds.min,
		max: bounds.max,
		minuteStep: minuteStepOf( options ),
		weekdays: weekdaysOf( element ),
		precision: precisionOf( element ),
		status: message ? 'error' : undefined,
	} : null;



	const badge = showIconBadge && cardMeta ? <CardIconSwatch card={cardMeta} /> : null;

	const fieldName = idPrefix + element.name;
	const commonProps = {
		elementName: fieldName,
		// An element written by a writer that skipped the label (an API key,
		// an ingest run) falls back to its name, as every other reader on the
		// platform does — an input with no caption beats a blank form.
		elementLabel: element.label?.singular || element.name,
		value,
		onChange: handleChange,
		labelBadge: badge,
		required: isRequired,
		// The author's help text rides the label (hover hint + "?" popover).
		help: elementHelpText( element ),
		// An element a group built from records reads, which this person
		// cannot change (keepr-api owner ruling b79; ItemForm's notice).
		// Explicit, not antd's disabled context: every input here passes its
		// own `disabled`, which the context cannot override.
		disabled,
	};

	// `options.scan` — the camera affordance. The lookup reads the raw option
	// itself (it has a record to resolve against, not just a string to
	// store), so only the text-shaped inputs are handed the normalized form.
	const scan = scanConfig( options );

	// A repeating group is not one input but a list of entries, each a row of
	// this very field per column (KPR-680).
	if ( isRepeatingGroup( element ) ) {
		return (
			<RepeatingGroupField
				group={element}
				value={value}
				onChange={onChange}
				item={event}
				cardMeta={cardMeta}
				storedElements={storedElements}
				disabled={disabled}
				errors={entryErrors}
				groupError={errorMessage}
				onClearErrors={onClearErrors}
				zone={zone}
				attachmentsOn={attachmentsOn}
				resolveLookupValue={resolveLookupValue}
				resetKey={resetKey}
			/>
		);
	}

	return (<>
		<div id={`element-field-${fieldName}`} className={'element-field' + ( message ? ' element-field--error' : '' )}>
			{ isCounted ? <ConfirmationsElementValue element={element} value={value} labelBadge={badge} /> : isSequenced ? <SequenceElementValue element={element} value={value} labelBadge={badge} /> : isRanked ? <RankElementValue element={element} labelBadge={badge} /> : isDriven ? <DrivenElementValue element={element} value={value} labelBadge={badge} event={event} cardMeta={cardMeta} groupColumn={groupColumn} /> : <>
				{ element.dataType === 'text-small' ? <InputText {...commonProps} masked={isMaskedElement( element )} disableAutocorrect={!!options?.disableAutocorrect} allowAutocomplete={allowAutocomplete} scan={scan} status={message ? 'error' : undefined} onBlur={handleBlur} count={textCount} /> : null}
				{ element.dataType === 'choice' && hasChoices && isMultiChoice ? <InputChoiceMulti {...commonProps} options={choices} /> : null}
				{ element.dataType === 'choice' && hasChoices && ! isMultiChoice ? (
					isSegmented
						? <InputSegmented {...commonProps} options={choices} />
						: <InputSelect {...commonProps} options={choices} selectIcons={hasSelectIcons} />
				) : null}
				{ element.dataType === 'choice' && ! hasChoices ? <InputText {...commonProps} allowAutocomplete={allowAutocomplete} /> : null}
				{ element.dataType === 'text-large' ? <InputTextArea {...commonProps} masked={isMaskedElement( element )} allowAutocomplete={allowAutocomplete} status={message ? 'error' : undefined} onBlur={handleBlur} count={textCount} /> : null}
				{ element.dataType === 'rich-text' ? <InputRichText {...commonProps} /> : null}
				{ isMultiDate ? (
					<MultiDateField
						element={element}
						value={value}
						onChange={handleChange}
						labelBadge={badge}
						required={isRequired}
						min={bounds.min}
						max={bounds.max}
						incrementButtons={!!options?.incrementButtons}
						showRelativeTime={!!options?.showRelativeTime}
						context={validationContext}
						disabled={disabled}
					/>
				) : null}
				{ element.dataType === 'date' && ! isMultiDate ? <InputDate {...commonProps} {...dateProps} incrementButtons={!!options?.incrementButtons} showRelativeTime={!!options?.showRelativeTime} /> : null}
				{ element.dataType === 'date-time' && ! isMultiDate ? <InputDate {...commonProps} {...dateProps} showTime={true} incrementButtons={!!options?.incrementButtons} showRelativeTime={!!options?.showRelativeTime} /> : null}
				{ element.dataType === 'time' ? <InputTime {...commonProps} {...dateProps} /> : null}
				{ [ 'number', 'decimal', 'integer', ].indexOf( element.dataType ) > -1 ? <InputNum {...commonProps} masked={isMaskedElement( element )} incrementButtons={!!options?.incrementButtons} dataType={element.dataType} options={options} status={message ? 'error' : undefined} onBlur={handleBlur} /> : null}
				{ element.dataType === 'boolean' ? <InputBoolean {...commonProps} element={element} /> : null}
				{ element.dataType === 'measurement' ? <InputMeasurement {...commonProps} element={element} cardId={normalizeId( cardMeta?._id )} /> : null}
				{ element.dataType === 'currency' ? <InputCurrency {...commonProps} element={element} status={message ? 'error' : undefined} onBlur={handleBlur} /> : null}
				{ element.dataType === 'phone' ? <InputPhone {...commonProps} element={element} status={message ? 'error' : undefined} onBlur={handleBlur} /> : null}
				{ element.dataType === 'email' ? <InputEmail {...commonProps} status={message ? 'error' : undefined} onBlur={handleBlur} /> : null}
				{ element.dataType === 'location' ? <InputLocation {...commonProps} element={element} status={message ? 'error' : undefined} onBlur={handleBlur} /> : null}
				{ element.dataType === 'url' ? <InputText {...commonProps} allowAutocomplete={allowAutocomplete} inputMode='url' scan={scan} status={message ? 'error' : undefined} onBlur={handleBlur} /> : null}
				{ element.dataType === 'rating' ? <InputRating {...commonProps} options={options} /> : null}
				{ element.dataType === 'color' ? <InputColor {...commonProps} value={value ?? ''} presets={COLOR_PRESETS} allowClear showText status={message ? 'error' : undefined} onBlur={handleBlur} /> : null}
				{ ! RENDERED_TYPES.includes( element.dataType ) ? <InputText {...commonProps} allowAutocomplete={allowAutocomplete} /> : null}
				{ element.dataType === 'card-lookup' ? (
					<InputCardLookup
						{...commonProps}
						collectionId={event.collection_id || event.collectionId}
						elementOptions={element.options || {}}
					/>
				) : null}
				{ element.dataType === 'file' ? (
					<InputFile
						{...commonProps}
						element={element}
						collectionId={normalizeId( event?.collection_id || event?.collectionId )}
						// The item's own card (its chain's merged elements), not the
						// ancestor that declared the element.
						cardId={normalizeId( event?.card_id || cardMeta?._id )}
						item={entryFile?.item || event}
						storedValue={storedElements ? storedElements[ element.name ] : undefined}
						attachmentsOff={attachmentsOn === false}
						group={entryFile?.group || null}
						entryKey={entryFile?.entryKey ?? null}
					/>
				) : null}
				{ element.dataType === 'user' ? (
					<InputUser
						{...commonProps}
						element={element}
						collectionId={event.collection_id || event.collectionId}
						status={message ? 'error' : undefined}
						onBlur={handleBlur}
					/>
				) : null}
			</> }

			{message ? <div className='element-field__error' role='alert'>{message}</div> : null}
		</div>

		{/* { element.dataType === 'number' ? <InputNumber controls={{upIcon: CHEVRON_UP, downIcon: CHEVRON_DOWN,}} min={1} max={20} value={value} onChange={handleChange} /> : null} */}
	</>);

};
